import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useHolidayMasterEntry } from './hooks/useHolidayMasterEntry';
import AppDateInput from '../../../components/AppDateInput';

export default function HolidayMasterEntryScreen() {
  const { state, actions } = useHolidayMasterEntry();
  const navigate = useNavigate();
  const { form, selectedGroupIds = [], availableGroups, isLoading, isSaving, isEdit, listPath } = state;

  if (isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-[1200px] flex-col items-center justify-center p-16">
        <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
        <p className="mt-3 text-xs font-semibold text-on-surface-variant">Loading Holiday Details...</p>
      </div>
    );
  }

  const allSelected =
    availableGroups.length > 0 &&
    availableGroups.every((g) => {
      const gid = typeof g === 'object' && g !== null ? Number(g.id) : Number(g);
      return selectedGroupIds.includes(gid);
    });

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-16">
      {/* Header Banner */}
      <section className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-outline-variant/10 pb-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-on-surface">
            {isEdit ? 'Holiday Master Edit' : 'Holiday Master Insert'}
          </h1>
        </div>

        <Link
          to={listPath}
          className="inline-flex items-center gap-1.5 rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-2 text-xs font-bold text-on-surface-variant hover:text-primary hover:bg-surface-container-low transition shadow-xs w-fit"
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          <span>Back to List</span>
        </Link>
      </section>

      {/* 2-Column Layout matching Screenshot 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Form Card */}
        <div className="lg:col-span-8 rounded-3xl border border-outline-variant/30 bg-surface-container-lowest shadow-sm overflow-hidden flex flex-col justify-between">
          {/* Card Header matching Screenshot 2: Group Details */}
          <div className="border-b border-outline-variant/20 bg-surface-container-low/40 px-8 py-4">
            <h2 className="font-display text-lg font-bold text-on-surface">Group Details</h2>
          </div>

          {/* Form Body matching Screenshot 2 */}
          <div className="p-8">
            <div className="max-w-2xl mx-auto space-y-5">
              {/* Name Field */}
              <div className="grid grid-cols-1 sm:grid-cols-[130px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Name <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => actions.patchForm('name', e.target.value)}
                  placeholder="Name"
                  className="h-10 w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-3.5 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>

              {/* Date Field */}
              <div className="grid grid-cols-1 sm:grid-cols-[130px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Date <span className="text-error">*</span>
                </label>
                <AppDateInput
                  value={form.date}
                  onChange={(val) => actions.patchForm('date', val)}
                  placeholder="dd-mm-yyyy"
                  inputClassName="h-10 text-xs bg-surface-container-low border-outline-variant/30 rounded-xl"
                />
              </div>

              {/* Group Field with Checkboxes */}
              <div className="grid grid-cols-1 sm:grid-cols-[130px_1fr] items-start gap-3 pt-2">
                <div className="sm:text-right">
                  <label className="text-xs font-bold text-on-surface-variant">
                    Group
                  </label>
                  {availableGroups.length > 1 && (
                    <div className="mt-1">
                      <button
                        type="button"
                        onClick={actions.toggleAllGroups}
                        className="text-[11px] font-bold text-primary hover:underline cursor-pointer"
                      >
                        {allSelected ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-2.5 bg-surface-container-low/60 rounded-2xl border border-outline-variant/25 p-4">
                  {availableGroups.length === 0 ? (
                    <p className="text-xs text-on-surface-variant">No employee groups configured.</p>
                  ) : (
                    availableGroups.map((group) => {
                      const gid = typeof group === 'object' && group !== null ? Number(group.id) : Number(group);
                      const gname = typeof group === 'object' && group !== null ? group.name : String(group);
                      const isChecked = selectedGroupIds.includes(gid);
                      return (
                        <label
                          key={gid}
                          className="flex items-center gap-3 p-2 rounded-xl hover:bg-surface-container-lowest/80 transition cursor-pointer select-none"
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => actions.toggleGroup(gid)}
                            className="h-4 w-4 rounded border-outline-variant/40 text-primary focus:ring-primary/25 cursor-pointer accent-primary"
                          />
                          <span className="text-xs font-medium text-on-surface">
                            {gname}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons at bottom right */}
          <div className="flex items-center justify-end gap-3 border-t border-outline-variant/15 bg-surface-container-low/40 px-8 py-4">
            <button
              type="button"
              disabled={isSaving}
              onClick={() => actions.handleSave({ exitAfter: true })}
              className="h-9 px-6 bg-[#0047ab] hover:bg-[#003788] text-white font-bold rounded-xl text-xs transition shadow-xs flex items-center gap-2 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-sm">save</span>
              <span>{isSaving ? 'Saving...' : 'Save & Exit'}</span>
            </button>

            {!isEdit && (
              <button
                type="button"
                disabled={isSaving}
                onClick={() => actions.handleSave({ exitAfter: false })}
                className="h-9 px-5 bg-surface-container-lowest hover:bg-surface-container-low text-primary font-bold rounded-xl text-xs transition cursor-pointer border border-outline-variant/40 disabled:opacity-60"
              >
                Save &amp; Continue
              </button>
            )}

            <button
              type="button"
              onClick={() => navigate(listPath)}
              className="h-9 px-5 bg-surface-container-high hover:bg-surface-container-highest text-on-surface font-bold rounded-xl text-xs transition cursor-pointer border border-outline-variant/20"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Right TIPS Panel */}
        <div className="lg:col-span-4 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-6 shadow-xs space-y-4">
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-on-surface">
            <span className="material-symbols-outlined text-base text-amber-500">lightbulb</span>
            <span>TIPS:</span>
          </div>

          <p className="text-xs text-on-surface-variant leading-relaxed font-medium">
            Holiday Master allows configuring organization-wide and group-specific holidays for daily attendance calculation, shift planning, and payroll processing.
          </p>

          <div className="pt-3 border-t border-outline-variant/15 space-y-2">
            <h4 className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
              CONFIGURATION GUIDE:
            </h4>
            <ul className="text-xs text-on-surface-variant space-y-2 list-disc list-inside leading-relaxed">
              <li>
                <strong className="text-on-surface">Name:</strong> Official title of the holiday (e.g., Republic Day, Diwali).
              </li>
              <li>
                <strong className="text-on-surface">Date:</strong> The calendar date on which the holiday is observed.
              </li>
              <li>
                <strong className="text-on-surface">Group:</strong> Choose which employee groups are entitled to this holiday. Unchecked groups will follow standard shift schedules.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
